import { useCallback, useRef, useState } from 'react';

/**
 * HOLD SAVE WHILE A DATE FIELD CAN'T READ WHAT'S IN IT.
 *
 * A DateField flags an entry it could not accept (an unreadable date, or one
 * outside its allowed range) and reports it through `onInvalidChange`. A form
 * with one or more date fields feeds each of those signals in here and gets back
 * one truth — is ANY date field currently flagged — to disable its Save button
 * and to guard its save handler.
 *
 * Two reads on purpose. `any` is React state, for the button's `disabled`. `ref`
 * is the same truth read synchronously, for the save handler: a field commits on
 * blur, and blurring by clicking Save fires that commit (and this flag) in the
 * same tick BEFORE the click handler runs, but the `disabled` re-render lands a
 * tick later — so the click still reaches the handler, where `ref.current` sees
 * the fresh flag and the state-driven `disabled` would not.
 */
export function useInvalidDates() {
  const flags = useRef<Record<string, boolean>>({});
  const [any, setAny] = useState(false);
  const ref = useRef(false);

  // One marker per field, made once and kept (stable identity so DateField's
  // effect deps don't churn). Pass its return to a DateField's onInvalidChange.
  const mark = useCallback((key: string) => (invalid: boolean) => {
    if (invalid) flags.current[key] = true;
    else delete flags.current[key];
    const now = Object.keys(flags.current).length > 0;
    ref.current = now;
    setAny(now);
  }, []);

  return { mark, any, ref };
}
